import { Fragment, useState, useId } from 'react';
import { Tag } from '@src/components/Tag/Tag';
import { Typography } from '@src/components/Typography/Typography';
import { useTheme } from '@src/theme/useTheme';
import { useTranslation } from 'react-i18next';
import { Button } from '@src/components/Button/Button';
import { RefreshIcon } from '@src/components/icons/RefreshIcon';
import { nameSuggestions, type NamePresetGroupId } from '@src/pages/NewBoxPage/namePresets';
import './NamePresetPicker.css';

export interface NamePresetPickerProps {
  value: string;
  year: number;
  onSelect: (name: string) => void;
  testId?: string;
}

export function NamePresetPicker({ value, year, onSelect, testId }: NamePresetPickerProps) {
  const { t } = useTranslation();
  const itemsId = useId();
  const [expandedGroups, setExpandedGroups] = useState<Partial<Record<NamePresetGroupId, boolean>>>(
    {},
  );
  const { seasonPackage } = useTheme();
  const groups: NamePresetGroupId[] = ['close', 'work'];
  if (seasonPackage.id === 'halloween') groups.push('halloween');

  return (
    <div className="name-preset-picker" data-testid={testId}>
      {groups.map((group) => {
        const label = t(`newBox.name.presetGroups.${group}`);
        const names = nameSuggestions(t, group, year);
        if (names.length === 0) return null;
        const expanded = expandedGroups[group] === true;
        const visibleNames = expanded ? names : names.slice(0, 6);
        const groupItemsId = `${itemsId}-${group}`;
        return (
          <Fragment key={group}>
            <Button
              className="name-preset-picker__generate"
              variant="secondary"
              size="small"
              icon={<RefreshIcon />}
              aria-label={t('newBox.name.generateName', { category: label })}
              testId={testId ? `${testId}-${group}` : `new-box-generate-${group}`}
              onClick={() => {
                const alternatives = names.filter((name) => name !== value);
                const candidates = alternatives.length ? alternatives : names;
                onSelect(candidates[Math.floor(Math.random() * candidates.length)]);
              }}
            >
              {label}
            </Button>
            <section className="name-preset-picker__group" aria-label={label}>
              <div className="name-preset-picker__head">
                <Typography as="span" role="title/eyebrow">
                  {label}
                </Typography>
                <span className="name-preset-picker__rule" aria-hidden="true" />
              </div>
              <div className="name-preset-picker__items" id={groupItemsId}>
                {visibleNames.map((name, index) => (
                  <Tag
                    key={name}
                    selected={value === name}
                    markSelected
                    onSelectedChange={() => onSelect(name)}
                    testId={testId ? `${testId}-${group}-${index}` : undefined}
                  >
                    {name}
                  </Tag>
                ))}
                {!expanded && names.length > 6 && (
                  <Button
                    variant="secondary"
                    size="small"
                    aria-label={t('newBox.name.showAll', { category: label })}
                    aria-expanded={expanded}
                    aria-controls={groupItemsId}
                    testId={testId ? `${testId}-${group}-more` : `new-box-names-${group}-more`}
                    onClick={() => setExpandedGroups((current) => ({ ...current, [group]: true }))}
                  >
                    <span aria-hidden="true">…</span>
                  </Button>
                )}
              </div>
            </section>
          </Fragment>
        );
      })}
    </div>
  );
}
